import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';

interface BlocoSoDivulgacaoProps {
  eventoId: number | null;
}

const AVISO_SEM_NUMERO =
  'Venda por fora ligada, mas sem WhatsApp. A página vai mostrar só o texto, sem o botão de chamar.';

const AVISO_REATIVAR =
  'Venda por fora desligada. Se quiser voltar a vender pela plataforma, ative de novo os tipos de inscrição em Editar evento.';

function lerAviso(data: unknown): string | null {
  if (typeof data === 'string' && data.trim() && data !== 'aviso') return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const v = obj.aviso ?? obj.mensagem;
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

export default function BlocoSoDivulgacao({ eventoId }: BlocoSoDivulgacaoProps) {
  const [ligado, setLigado] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  const [comoParticipar, setComoParticipar] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  function aoAlternar() {
    if (ligado) {
      if (!eventoId) {
        setLigado(false);
        setSalvo(false);
        return;
      }
      void aoDesmarcar();
    } else {
      setLigado(true);
      setErro(null);
      setAviso(null);
      setSalvo(false);
    }
  }

  async function aoDesmarcar() {
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      const { data, error } = await supabase.rpc('marcar_so_divulgacao', {
        p_evento: eventoId,
        p_ligado: false,
      });
      if (error) throw error;
      setLigado(false);
      setSalvo(false);
      if (data === 'aviso') setAviso(AVISO_REATIVAR);
      else {
        const msg = lerAviso(data);
        if (msg) setAviso(msg);
      }
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível desmarcar.'));
    } finally {
      setSalvando(false);
    }
  }

  async function aoSalvar() {
    if (!eventoId) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    setSalvo(false);
    try {
      const { data, error } = await supabase.rpc('marcar_so_divulgacao', {
        p_evento: eventoId,
        p_ligado: true,
        p_whatsapp: whatsapp,
        p_como_participar: comoParticipar,
      });
      if (error) throw error;

      if (data === 'aviso') {
        setAviso(AVISO_SEM_NUMERO);
        setSalvo(true);
        return;
      }

      const obj = (data ?? {}) as { ok?: boolean; erro?: string };
      if (obj.ok === false) {
        setErro(obj.erro || 'Não foi possível salvar.');
        return;
      }

      const msg = lerAviso(data);
      if (msg) setAviso(msg);
      setSalvo(true);
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível salvar.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-lg border border-secondary-200 bg-white p-5">
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={ligado}
          onChange={aoAlternar}
          disabled={salvando}
          className="mt-0.5 h-4 w-4 cursor-pointer text-primary-500 disabled:cursor-not-allowed"
        />
        <span className="min-w-0">
          <span className="block text-base font-medium text-foreground-950">
            Quem quiser participar fala direto com a gente
          </span>
          <span className="mt-1 block text-sm text-secondary-500">
            Marque se a venda é por fora da plataforma — ingresso na mão, cartela na secretaria,
            reserva por telefone.
          </span>
        </span>
      </label>

      {ligado && (
        <div className="mt-5 space-y-5 border-t border-secondary-100 pt-5">
          <CampoTexto
            id="whatsapp-so-divulgacao"
            name="whatsapp-so-divulgacao"
            label="WhatsApp de quem vende"
            help="É para lá que o botão da página vai levar."
            value={whatsapp}
            onChange={setWhatsapp}
            inputMode="tel"
            placeholder="(14) 99788-9732"
          />
          <CampoArea
            id="como-participar-so-divulgacao"
            name="como-participar-so-divulgacao"
            label="O que escrever na página"
            help="Duas linhas, no máximo."
            value={comoParticipar}
            onChange={setComoParticipar}
            placeholder="A cartela é vendida direto com a organização. Chame no WhatsApp e garanta a sua!"
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={aoSalvar}
              disabled={salvando || !eventoId}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-500"
            >
              {salvando && (
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
              )}
              Salvar
            </button>
            {salvo && !erro && (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-line" />
                </span>
                salvo
              </span>
            )}
          </div>
        </div>
      )}

      {erro && (
        <p className="mt-3 rounded-md border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm text-danger-700">
          {erro}
        </p>
      )}

      {aviso && !erro && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
            <i className="ri-information-line text-lg" />
          </span>
          <p className="text-sm text-amber-800">{aviso}</p>
        </div>
      )}
    </div>
  );
}